import {FC, ReactElement} from 'react';
import {Link} from 'react-router-dom';
import {usePlayback} from 'contexts/WatchContext';
import {VideoSubjectMetadataMessage} from '@/types/VideoSubjectMetadata';
import {Avatar, Box, CardMedia, Typography, useTheme, useMediaQuery} from '@mui/material';
import { getColorFromHash } from 'utils/ColorUtils';
import { getVideoMSizeThumb, getVideoLSizeThumb, getOwnerMSizeThumb} from 'utils/VideoUtils';
import {AutoAwesome} from '@mui/icons-material';
import HeartIcon from 'components/HeartIcon';

// Video items now use VideoSubjectMetadata from proto definitions

interface VideoSubjectProps {
  video: VideoSubjectMetadataMessage;
  videoLink: { to: string; state?: any };
  ownerLink: { to: string };
}

/**
 * Card representing a video, with clickable thumbnail and chips for title & owner.
 */

const VideoSubject: FC<VideoSubjectProps> = ({
  video,
  videoLink,
  ownerLink,
}): ReactElement => {
  const { currentVideo } = usePlayback();
  const isActive = currentVideo?.musicVideo === video.musicVideo;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  // size (px) for crown, heart, and magic indicator icons
  const iconSize = 24;
  // desired center distance from edge to icon center: theme.spacing(2) (16px)
  const center = theme.spacing(2);
  // offset for top/right or bottom/right = center - half icon
  const offset = `calc(${center} - ${iconSize/2}px)`;
  return (
  <Box>
    <Box component={Link} to={videoLink.to} state={videoLink.state}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        borderRadius: { xs: 0, sm: 2 },
        // highlight active playing video with left red bar
        ...(isActive && {
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: '4px',
            bgcolor: 'primary.main',
            zIndex: 2,
          }
        })
      }}
      >
      <CardMedia
        component="img"
        image={isDesktop ? getVideoLSizeThumb(video) : getVideoMSizeThumb(video)}
        alt=  {`${video.trackTitle} - ${video.ownerLabel}`}
        title={`${video.trackTitle} - ${video.ownerLabel}`}
        sx={{
          backgroundColor: '#eee',
          borderRadius: { xs: 0, sm: 2 },
          width: '100%',
          height: 'auto',
          display: 'block'
        }}
      />
      {/* Status indicator: magic video or user-saved heart toggle */}
      {video.magicVideo && !video.videoHeart ? (
        // Magic indicator
        <AutoAwesome
          sx={{
            position: 'absolute',
            right: offset,
            bottom: offset,
            color: 'gold',
            fontSize: iconSize,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        />
      ) : video.videoHeart > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            bottom: offset,
            right: offset,
            width: iconSize,
            height: iconSize,
            zIndex: 3,
            pointerEvents: 'none',
          }}
        >
          <HeartIcon filled size={iconSize} />
        </Box>
      ) : null}
    </Box>
    <Box display="flex" alignItems="flex-start" sx={{ pt: 1, minWidth: 0, pl: { xs: 1, md: 0 } }}>
      {/* Owner avatar */}
      <Avatar component={Link} to={ownerLink.to}
        src={getOwnerMSizeThumb(video)}
        alt={video.ownerTitle}
        sx={{
          width: 40,
          height: 40,
          cursor: 'pointer',
          textDecoration: 'none',
          bgcolor: getColorFromHash(video.ownerTitle),
          color: 'rgba(0, 0, 0, 0.87)',
        }}
      />
      <Box sx={{ pl: 1, flex: 1, minWidth: 0 }}>
        {/* Track title (two lines max) */}
        <Typography component={Link} to={videoLink.to} state={videoLink.state}
          variant="subtitle1" noWrap
          sx={{
            color: 'inherit',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.2,
            minWidth: 0,
          }}
        >
          {video.trackTitle}
        </Typography>
        {/* Owner title */}
        <Typography component={Link} to={ownerLink.to}
          variant="body2" color="text.secondary" noWrap
          sx={{
            color: 'inherit',
            textDecoration: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
            mt: 0.3,
            lineHeight: 1.2,
          }}
        >
          {video.ownerLabel}
        </Typography>
      </Box>
    </Box>
  </Box>
  );
};

export default VideoSubject;